import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../auth/Auth';
import { useNavigate } from 'react-router-dom';
import './Login.css';

function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [attempts, setAttempts] = useState(0);
  const [lockoutUntil, setLockoutUntil] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const savedLockout = localStorage.getItem('loginLockoutUntil');
    if (savedLockout) {
      const lockoutTime = parseInt(savedLockout);
      if (lockoutTime > Date.now()) {
        setLockoutUntil(lockoutTime);
        const remainingMinutes = Math.ceil((lockoutTime - Date.now()) / (60 * 1000));
        setError(`Занадто багато спроб. Спробуйте через ${remainingMinutes} хвилин`);
      } else {
        localStorage.removeItem('loginLockoutUntil');
        localStorage.removeItem('loginAttempts');
      }
    }
    
    const savedAttempts = localStorage.getItem('loginAttempts');
    if (savedAttempts) {
      setAttempts(parseInt(savedAttempts));
    }
  }, []);

  const handleLockout = (attempts) => {
    const now = Date.now();
    let lockoutDuration;
    let message = '';
    
    if (attempts >= 3 && attempts < 5) {
      lockoutDuration = 2 * 60 * 1000; 
      message = 'Забагато невдалих спроб. Спробуйте через 2 хвилини';
    } else if (attempts >= 5) {
      lockoutDuration = 10 * 60 * 1000; 
      message = 'Забагато невдалих спроб. Спробуйте через 10 хвилин';
    }

    if (lockoutDuration) {
      const lockoutUntil = now + lockoutDuration;
      setLockoutUntil(lockoutUntil);
      setError(message);
      localStorage.setItem('loginLockoutUntil', lockoutUntil.toString());
    }
  };

  const handleLogin = async (email, password) => {
    if (lockoutUntil && Date.now() < lockoutUntil) {
      const remainingMinutes = Math.ceil((lockoutUntil - Date.now()) / (60 * 1000));
      setError(`Зачекайте ${remainingMinutes} хвилин перед наступною спробою`);
      return;
    }

    setIsLoading(true);
    try {
      const response = await axios.post('https://hub2.daolog.net/login', { email, password });
      
      if (response.data.deactivated) {
        setError('Ваш акаунт було деактивовано');
        setIsLoading(false);
        return;
      }
      
      if (response.data.success === false) {
        const newAttempts = attempts + 1;
        setAttempts(newAttempts);
        localStorage.setItem('loginAttempts', newAttempts.toString());
        handleLockout(newAttempts);
        if (newAttempts < 3) {
          setError(response.data.message || 'Невірний email або пароль');
        }
        setIsLoading(false);
        return;
      }

      setAttempts(0);
      localStorage.removeItem('loginAttempts');
      localStorage.removeItem('loginLockoutUntil');
      
      await login(email, password);
      window.location.href = '/';
    } catch (error) {
      console.error('Login failed:', error);
      setError('Помилка підключення до сервера');
      setIsLoading(false);
    }
  };

  function handleSubmit(event) {
    event.preventDefault();
    setError('');

    if (!email || !password) {
      setError('Будь ласка, заповніть усі поля');
      return;
    }

    handleLogin(email, password);
  }

  const handleForgotPassword = () => {
    navigate('/ForgotPassword');
  };

  const getLockoutMessage = () => {
    if (!lockoutUntil) return null;
    const remainingTime = lockoutUntil - Date.now();
    if (remainingTime <= 0) return null;
    
    const minutes = Math.floor(remainingTime / 60000);
    const seconds = Math.floor((remainingTime % 60000) / 1000);
    
    return `Заблокувано ще на ${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  return (
    <div className="login-page">
      <div className="login-container">
        <div className="login-header">
          <h1 className="login-title">Вхід</h1>
          <p className="login-subtitle">Увійдіть у свій акаунт</p>
        </div>
        
        <div className="login-card">
          <form onSubmit={handleSubmit} className="login-form">
            <div className="form-group">
              <label htmlFor="email" className="form-label">
                Email
              </label>
              <input
                id="email"
                className="form-input"
                type="email"
                placeholder="Ваш email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                required
                disabled={isLoading || (lockoutUntil && Date.now() < lockoutUntil)}
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="password" className="form-label">
                Пароль
              </label>
              <input
                id="password"
                className="form-input"
                type="password"
                placeholder="Ваш пароль"
                value={password}
                onChange={e => setPassword(e.target.value)}
                required
                disabled={isLoading || (lockoutUntil && Date.now() < lockoutUntil)}
              />
            </div>
            
            {error && (
              <div className="error-message">
                {error}
                {lockoutUntil && Date.now() < lockoutUntil && (
                  <div className="lockout-timer">
                    {getLockoutMessage()}
                  </div>
                )}
              </div>
            )}
            
            <button 
              type="submit" 
              className="login-button"
              disabled={isLoading || (lockoutUntil && Date.now() < lockoutUntil)}
            >
              {isLoading ? (
                <span className="button-loading">
                  <span className="spinner"></span>
                  Завантаження...
                </span>
              ) : (
                'Увійти'
              )}
            </button>
            
            <div className="login-footer">
              <button 
                type="button"
                className="forgot-password-button"
                onClick={handleForgotPassword}
              >
                Забули пароль?
              </button>
            </div>
          </form>
        </div>
        
       
      </div>
    </div>
  );
}

export default Login;